/**
 * Validate a post-login `redirectUrl` candidate and return it UNCHANGED, or `null` when it is not a
 * safe same-origin destination. Both login sinks assign this result straight to
 * `globalThis.location.href`, so anything returned here is a navigation the browser will perform.
 *
 * WHY a URL-origin comparison and not a stack of string-prefix checks: the WHATWG parser strips
 * ASCII tab/LF/CR from ANYWHERE in the input before parsing, while `String.prototype.startsWith`
 * sees them — so `/<TAB>//evil.example`, reachable as `?redirectUrl=/%09//evil.example` because
 * `searchParams.get()` decodes, passes a `startsWith('//')` guard and still resolves to
 * `https://evil.example`. Mirrors `buildOrgScopedUrl` (src/server/org-data.ts:62-86), the repo's existing
 * off-origin guard — except that one throws, while this returns `null` so each sink falls through
 * to its portal home. Consolidates the two divergent inline guards that commit 1e86ca09e left
 * desynced. [APX-387]
 *
 * Every clause is deliberate:
 * - PATH-FORM ONLY. The value must start with `/`. This keeps `useUserProfile`'s `setRedirectUrl`
 *   (which stores an ABSOLUTE `window.location.href`) exactly as rejected as it is today — zero
 *   behaviour change — and it closes the divergence where `''` or a bare `evil.example` would be
 *   checked against `location.origin` but resolved by the sink against the document URL. It is also
 *   what makes `location.origin` (path-less) a sound stand-in for that document base: for
 *   path-absolute input the base path cannot change the result. Restoring that deep-link resume is
 *   a PRODUCER fix (store `pathname + search`), never a widening of what this accepts.
 * - `//` IS REJECTED UP FRONT, redundantly. The origin comparison below is the security mechanism;
 *   this clause exists only so the LITERAL `//` prefix is rejected ORIGIN-INDEPENDENTLY (without it
 *   the verdict flips with the ambient origin — `//localhost/evil` is same-origin under jsdom's
 *   `http://localhost`). It does NOT cover the backslash or control-char protocol-relative forms:
 *   `/\<our-host>/evil` is same-origin and is ACCEPTED, decided by the origin comparison.
 * - `new URL` IS WRAPPED. It throws `TypeError: Invalid URL` on host-less authority forms such as
 *   `/\`, `/\\`, `/\[`. At Login.tsx the guard runs ABOVE `if (result.success)`, so an escaping
 *   throw would break the submit handler on the failure path as well as the success path.
 * - USERINFO IS REJECTED. `/\evil.example@<our-host>/` parses SAME-ORIGIN with
 *   `username === 'evil.example'`. Not an off-origin redirect, but a credential-prompting,
 *   display-confusing URL on our own domain. Two tokens to reject — do not delete it as cargo cult.
 * - THE RAW INPUT IS RETURNED, never `url.pathname + url.search`. Normalising is both unsafe and
 *   lossy: `/..//evil.example` is same-origin when parsed but normalises to `//evil.example`, which
 *   the sink then resolves OFF-ORIGIN; and normalising re-encodes spaces/quotes and drops a
 *   trailing bare `?`, mangling the ORI-730 Laravel signed onboarding URL that must survive the
 *   login bounce byte-for-byte. Reject, never rewrite.
 *
 * Pure, and free of module-scope globals on purpose: `src/shared/utils/` modules are pulled into
 * server-rendered graphs and the `(auth)` group prerenders, so a top-level `location` read would
 * break the build. With no `window` it fails CLOSED — degrading to "no redirect", never to
 * "redirect allowed".
 */
export const safeInternalRedirect = (raw: string | null | undefined): string | null => {
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) {
    return null;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  const origin = window.location.origin;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || url.username || url.password) {
    return null;
  }
  return raw;
};
